import { ref } from 'vue';
import { useFormStore } from '@/store/formStore';
import { useFormGeneration } from './useFormGeneration';
import productService from '@shared/services/productService';

/**
 * Composable voor het beheren van configurator migraties
 * Detecteert product wijzigingen en biedt migratie opties
 */
export const useConfiguratorMigration = () => {
  const formStore = useFormStore();
  const { regenerateProductPages, detectProductChanges } = useFormGeneration();

  const migrationInProgress = ref(false);
  const detectedChanges = ref([]);

  /**
   * Detecteert of een configurator migratie nodig heeft
   */
  const checkMigrationNeeded = async (configuratorId) => {
    try {
      const form = await formStore.getFormById(configuratorId);
      if (!form || form.type !== 'configurator') {
        return { needed: false, changes: [] };
      }

      const selectedProducts = form.settings?.cpq?.selectedProducts || [];
      const changes = [];

      for (const productId of selectedProducts) {
        try {
          const currentProduct = await productService.getProductById(productId);
          const formStoredProduct = getStoredProductData(form, productId);

          if (!currentProduct) {
            changes.push({
              type: 'product-removed',
              productId,
              message: `Product ${productId} is niet meer beschikbaar`,
              severity: 'error',
            });
            continue;
          }

          const productChanges = detectProductChanges(formStoredProduct, currentProduct);
          if (productChanges.length > 0) {
            changes.push({
              type: 'product-modified',
              productId,
              productName: currentProduct.name,
              changes: productChanges,
              severity: 'warning',
            });
          }
        } catch (error) {
          changes.push({
            type: 'product-error',
            productId,
            message: `Fout bij laden van product: ${error.message}`,
            severity: 'error',
          });
        }
      }

      return {
        needed: changes.length > 0,
        changes,
        configuratorId,
        configuratorName: form.name,
      };
    } catch (error) {
      console.error('Failed to check migration:', error);
      return { needed: false, changes: [], error: error.message };
    }
  };

  /**
   * Voert automatische migratie uit
   */
  const performAutoMigration = async (configuratorId, migrationOptions = {}) => {
    try {
      migrationInProgress.value = true;

      const form = await formStore.getFormById(configuratorId);
      const selectedProducts = form.settings?.cpq?.selectedProducts || [];

      // Filter out removed products if requested
      let updatedProducts = selectedProducts;
      if (migrationOptions.removeDeletedProducts) {
        const validProducts = [];
        for (const productId of selectedProducts) {
          try {
            const product = await productService.getProductById(productId);
            if (product) {
              validProducts.push(productId);
            }
          } catch (error) {
            console.warn(`Product ${productId} removed from configurator`);
          }
        }
        updatedProducts = validProducts;
      }

      // Regenerate pages for updated products
      const newPages = [];

      // Keep product selection page
      const productSelectionPage = form.pages.find((p) => p.id === 'product-selection');
      if (productSelectionPage) {
        newPages.push({
          ...productSelectionPage,
          elements: productSelectionPage.elements.map((el) => ({
            ...el,
            props: {
              ...el.props,
              availableProducts: updatedProducts,
            },
          })),
        });
      }

      // Regenerate product option pages
      for (const productId of updatedProducts) {
        try {
          const product = await productService.getProductById(productId);
          if (product) {
            const productPages = await regenerateProductPages(product);
            newPages.push(...productPages);
          }
        } catch (error) {
          console.error(`Failed to regenerate pages for product ${productId}:`, error);
        }
      }

      // Keep quote summary page
      const quoteSummaryPage = form.pages.find((p) => p.id === 'quote-summary');
      if (quoteSummaryPage) {
        newPages.push(quoteSummaryPage);
      }

      // Update form
      const updatedForm = {
        ...form,
        pages: newPages,
        settings: {
          ...form.settings,
          cpq: {
            ...form.settings.cpq,
            selectedProducts: updatedProducts,
            lastMigration: new Date().toISOString(),
            migrationVersion: (form.settings.cpq.migrationVersion || 0) + 1,
          },
        },
      };

      await formStore.updateForm(configuratorId, updatedForm);

      return {
        success: true,
        updatedProducts: updatedProducts.length,
        removedProducts: selectedProducts.length - updatedProducts.length,
        regeneratedPages: newPages.length,
      };
    } catch (error) {
      console.error('Auto migration failed:', error);
      return {
        success: false,
        error: error.message,
      };
    } finally {
      migrationInProgress.value = false;
    }
  };

  /**
   * Voert handmatige migratie uit met gebruiker keuzes
   */
  const performManualMigration = async (configuratorId, userChoices) => {
    try {
      migrationInProgress.value = true;

      const form = await formStore.getFormById(configuratorId);
      const newPages = [];

      // Apply user choices per product
      for (const choice of userChoices) {
        if (choice.action === 'keep') {
          // Keep existing pages for this product
          const existingPages = form.pages.filter((p) => p.productId === choice.productId);
          newPages.push(...existingPages);
        } else if (choice.action === 'regenerate') {
          // Regenerate pages for this product
          try {
            const product = await productService.getProductById(choice.productId);
            if (product) {
              const productPages = await regenerateProductPages(product);
              newPages.push(...productPages);
            }
          } catch (error) {
            console.error(`Failed to regenerate product ${choice.productId}:`, error);
          }
        }
        // 'remove' action = don't add pages
      }

      // Always keep non-product pages
      const systemPages = form.pages.filter(
        (p) => p.id === 'product-selection' || p.id === 'quote-summary' || !p.productId
      );
      newPages.unshift(...systemPages);

      // Update form
      const updatedForm = {
        ...form,
        pages: newPages,
        settings: {
          ...form.settings,
          cpq: {
            ...form.settings.cpq,
            lastMigration: new Date().toISOString(),
            migrationVersion: (form.settings.cpq.migrationVersion || 0) + 1,
          },
        },
      };

      await formStore.updateForm(configuratorId, updatedForm);

      return { success: true };
    } catch (error) {
      console.error('Manual migration failed:', error);
      return { success: false, error: error.message };
    } finally {
      migrationInProgress.value = false;
    }
  };

  /**
   * Haalt opgeslagen product data uit form metadata
   */
  const getStoredProductData = (form, productId) => {
    // Try to find stored product data in form metadata
    const storedProducts = form.settings?.cpq?.storedProducts || {};
    return storedProducts[productId] || null;
  };

  /**
   * Slaat huidige product data op in form metadata voor toekomstige vergelijking
   */
  const storeProductSnapshot = async (configuratorId, productId) => {
    try {
      const product = await productService.getProductById(productId);
      if (!product) return;

      const form = await formStore.getFormById(configuratorId);
      const updatedForm = {
        ...form,
        settings: {
          ...form.settings,
          cpq: {
            ...form.settings.cpq,
            storedProducts: {
              ...form.settings.cpq.storedProducts,
              [productId]: {
                id: product.id,
                name: product.name,
                options: product.options,
                snapshotDate: new Date().toISOString(),
              },
            },
          },
        },
      };

      await formStore.updateForm(configuratorId, updatedForm);
    } catch (error) {
      console.error('Failed to store product snapshot:', error);
    }
  };

  /**
   * Genereert migratie rapport
   */
  const generateMigrationReport = (changes) => {
    const report = {
      summary: {
        totalChanges: changes.length,
        errors: changes.filter((c) => c.severity === 'error').length,
        warnings: changes.filter((c) => c.severity === 'warning').length,
      },
      recommendations: [],
    };

    // Add recommendations based on changes
    if (report.summary.errors > 0) {
      report.recommendations.push({
        type: 'critical',
        message: 'Er zijn kritieke problemen gevonden die handmatige actie vereisen.',
        action: 'manual-review',
      });
    }

    if (report.summary.warnings > 0 && report.summary.errors === 0) {
      report.recommendations.push({
        type: 'suggestion',
        message: 'Automatische migratie is mogelijk, maar controleer de wijzigingen.',
        action: 'auto-migrate',
      });
    }

    return report;
  };

  return {
    // State
    migrationInProgress,
    detectedChanges,

    // Methods
    checkMigrationNeeded,
    performAutoMigration,
    performManualMigration,
    storeProductSnapshot,
    generateMigrationReport,
  };
};
